/**
 * `client/src/audio/ambiances.ts` : quelle ambiance jouer, et à quelle vitesse, pour CHAQUE
 * zone de santé mentale (⟦A-160⟧, C35).
 *
 * Nommé `ambiances.ts`, et pas `ambianceSM.ts` : ce nom-là ne diffère de `AmbianceSM.tsx` (le
 * composant) que par la casse d'une lettre, et TypeScript refuse deux fichiers dont les noms ne
 * diffèrent QUE par la casse (ça casserait sur un système de fichiers sensible à la casse,
 * même si macOS l'accepte en local) : d'où ce nom volontairement différent.
 *
 * **Module PUR**, même contrat que `audio/beatmap.ts` et `audio/sons.ts` : ni DOM, ni React, ni
 * Web Audio. `AmbianceSM.tsx` ne fait qu'appeler `pisteAmbianceSM(zone)` et obéir au résultat
 * (cette fonction ne joue rien, elle DÉCIDE, et c'est ce qui la rend vérifiable sans navigateur).
 *
 * ## Retour de Matt (18/09/2026), verbatim
 *
 * « Ah mais tu as mis une musique quand on est dans le mal, c'est trop bien. Par contre je
 * voudrais cette musique de fond quand on est normal, et une autre un peu plus sombre quand on
 * est fatigué. » ⟦A-160⟧ étend donc l'ambiance UNIQUE de la zone rouge (⟦A-76⟧) à une échelle
 * des quatre zones :
 *
 * | zone      | morceau            | vitesse | pourquoi |
 * |-----------|--------------------|---------|----------|
 * | `vert`    | `sm_rouge_loop`    | ×1      | le morceau qui plaisait déjà à Matt (*Lost in Perspective*, StreamBeats) : inchangé, juste déplacé de la zone rouge à la zone verte |
 * | `fatigue` | `sm_fatigue_loop`  | ×0,92   | un cran plus sombre (*Midnight Stroll*, même album StreamBeats « In 4k ») : un ralenti léger, pas un coup de frein |
 * | `rouge`   | `sm_tension_loop`  | ×0,85   | encore plus sombre et tendu (*Dissonant Whispers*, StreamBeats) : le ralenti ⟦A-76⟧ d'origine, inchangé |
 * | `crise`   | rien (`null`)      | (aucune) | silence, c'est l'écran qui parle (⟦A-76⟧, inchangé) |
 *
 * Le choix des morceaux, leur mesure (BPM, niveau, registre) et leur provenance sont détaillés
 * dans `public/audio/PROVENANCE.md` (§ « L'échelle des ambiances »).
 *
 * ⚠️ **État neutre (⟦A-22⟧)** : cette fonction ne lit qu'un `ZoneSM`, elle ne lit ni score, ni
 * durée, ni tirage : et rien ne l'attend (retour `void`-compatible en `null`, jamais une
 * exception). Le son ne décide jamais.
 */

import type { ZoneSM } from "../game/format";

/** Le ralenti ⟦A-76⟧ de la zone rouge, inchangé par C35 : la tension maximale reste ×0,85. */
export const RALENTI_ZONE_ROUGE = 0.85;

/**
 * ⟦A-160⟧ Le ralenti de la zone fatigue : un cran, pas un coup de frein. ×0,92 n'est pas
 * arbitraire : `sm_fatigue_loop` a un tempo natif de 77,0 BPM ; étiré à ×0,92 il tombe à
 * 70,8 BPM, à moins d'un dixième du tempo natif de `sm_rouge_loop` (70,7 BPM). La zone fatigue
 * *sonne* donc comme une variante ralentie de la zone verte, pas comme une autre chanson qui
 * traînerait les pieds (détail du calcul : `public/audio/PROVENANCE.md`).
 */
export const RALENTI_ZONE_FATIGUE = 0.92;

/** Le volume bas de toute ambiance de zone (⟦A-76⟧, inchangé) : elle pèse, elle ne domine pas. */
export const VOLUME_AMBIANCE_SM = 0.35;

/** Une piste d'ambiance choisie pour une zone : son id de manifeste et sa vitesse de lecture. */
export interface PisteAmbianceSM {
  readonly id: string;
  readonly playbackRate: number;
}

/**
 * ⟦A-160⟧ La sélection : un id de morceau et une vitesse par zone, `null` en zone `crise`
 * (silence, ⟦A-76⟧). C'est TOUTE la décision, et `AmbianceSM.tsx` ne fait qu'obéir : démarrer ce
 * qui manque, remplacer ce qui a changé, se taire si `null`, et ne jamais voler le canal à un
 * autre écran (la règle du canal unique reste dans le composant, pas ici).
 */
export function pisteAmbianceSM(zone: ZoneSM): PisteAmbianceSM | null {
  switch (zone) {
    case "vert":
      return { id: "sm_rouge_loop", playbackRate: 1 };
    case "fatigue":
      return { id: "sm_fatigue_loop", playbackRate: RALENTI_ZONE_FATIGUE };
    case "rouge":
      return { id: "sm_tension_loop", playbackRate: RALENTI_ZONE_ROUGE };
    case "crise":
      return null;
  }
}

/**
 * Les ids que `AmbianceSM` possède : ceux-là seuls, elle a le droit de les arrêter ou de les
 * remplacer en fondu. Tout autre id en cours (piste du Montage, fanfare, mémorial) lui est
 * étranger : le canal unique dit qu'elle ne le touche jamais.
 */
export const IDS_AMBIANCE_SM: ReadonlySet<string> = new Set(
  (["vert", "fatigue", "rouge"] as const).map((zone) => pisteAmbianceSM(zone)!.id),
);
